import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

export default function VerifyPage() {
  const location = useLocation();
  const email = location.state?.email;
  const [isResending, setIsResending] = useState(false);
  const { toast } = useToast();

  const handleResend = async () => {
    if (!email) return;
    
    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) throw error;

      toast({
        title: "Email sent",
        description: "A new verification email has been sent to your inbox.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to resend verification email",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex-1 flex flex-col justify-start py-24 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <Mail className="h-6 w-6 text-indigo-600" />
            </div>
            
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
              Check your inbox
            </h2>
            
            {email && (
              <p className="mt-2 text-sm text-gray-600">
                We've sent a verification link to{' '}
                <span className="font-medium text-indigo-600">{email}</span>
              </p>
            )}
            
            <div className="mt-4 text-sm text-gray-500">
              Click the link in the email to verify your account. 
              The link will expire in 24 hours.
            </div>

            <div className="mt-8 space-y-4">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open('https://gmail.com', '_blank', 'noopener,noreferrer')}
              >
                Open Gmail
              </Button>

              <Link to="/auth" className="block">
                <Button
                  variant="ghost"
                  className="w-full text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to login
                </Button>
              </Link>
            </div>

            <div className="mt-8 text-sm text-gray-500">
              Didn't receive the email?{' '}
              <Button
                variant="link"
                className="text-indigo-600 hover:text-indigo-500 p-0"
                onClick={handleResend}
                disabled={isResending}
              >
                {isResending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Click to resend'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 